import CaasStorageKeys from '../constants/caas.storage.keys';
import { TokenEvent } from '../sdk/token.event';
import { ConsoleEvent } from '../sdk/console.event';

export default class FetchUtils {
  static Methods = {
    GET: 'get',
    POST: 'post',
    DELETE: 'delete',
    PUT: 'put',
  };
  static MissingTokenMessage = 'MISSING_AUTH_TOKEN';
  static RefreshTokenTimeoutInMs = 30000;
  static isRefreshingToken = false;
  static refreshPromise = null;

  static getHeaders(headers = {}) {
    return new Headers({
      authorization: `Bearer ${
        window.__CAAS__.config[CaasStorageKeys.CONFIG].token
      }`,
      'x-lang': window.__CAAS__.config[CaasStorageKeys.CONFIG].lang,
      'x-country': window.__CAAS__.config[CaasStorageKeys.CONFIG].country,
      'x-app': window.__CAAS__.config[CaasStorageKeys.CLIENT].id,
      'x-app-version':
        window.__CAAS__.config[CaasStorageKeys.CLIENT].appVersion,
      'x-sdk-version':
        window.__CAAS__.config[CaasStorageKeys.CLIENT].sdkVersion,
      'x-device-os': window.__CAAS__.config[CaasStorageKeys.DEVICE].os,
      'x-os-version': window.__CAAS__.config[CaasStorageKeys.DEVICE].osVersion,
      'content-type': 'application/json',
      ...window.__CAAS__.config[CaasStorageKeys.CONFIG].headers,
      ...headers,
    });
  }

  static getQueryParams(params = {}) {
    const query = new URLSearchParams();
    Object.keys(params)
      .filter(key => ![null, undefined, ''].includes(params[key]))
      .forEach(key => {
        if (Array.isArray(params[key]) && params[key].length) {
          query.set(key, params[key].join(','));
        } else {
          query.set(key, params[key]);
        }
      });
    return `?${query.toString()}`;
  }

  static async parseResponse(response) {
    let body = await response.text();

    try {
      // ATTENTION: In case response code is 204: NO CONTENT, the "await response.json()" breaks. This cover that case
      body = JSON.parse(body);
    } catch (e) {}

    return new Promise((resolve, reject) => {
      if (!response.ok) {
        return reject({
          statusCode: response.status,
          body,
        });
      }
      resolve(body);
    });
  }

  static storeToken(newToken) {
    window.__CAAS__.config[CaasStorageKeys.CONFIG].token = newToken;
    const config =
      JSON.parse(localStorage.getItem(CaasStorageKeys.CONFIG)) || {};
    config.token = newToken;
    localStorage.setItem(CaasStorageKeys.CONFIG, JSON.stringify(config));
  }

  static async refreshToken() {
    if (!this.isRefreshingToken) {
      ConsoleEvent.log('Executing refresh token');
      this.isRefreshingToken = true;
      this.refreshPromise = new Promise((resolve, reject) => {
        const retryListener = async ({ detail }) => {
          ConsoleEvent.log('Executing retry listener');

          if (detail.eventId !== refreshId) {
            ConsoleEvent.log(
              'WARNING: Skipping refresh token because it is not the same event id.',
            );
            return;
          }

          clearTimeout(timeoutId);

          document.removeEventListener(
            window.__CAAS__.events.refreshToken,
            retryListener,
          );
          ConsoleEvent.log('Removed refresh listener');

          const newToken = detail.eventData.access_token;
          this.storeToken(newToken);
          this.isRefreshingToken = false;
          ConsoleEvent.log('Refresh token finsihed successfully');
          resolve(newToken);
        };

        document.addEventListener(
          window.__CAAS__.events.refreshToken,
          retryListener,
        );

        ConsoleEvent.log('Added refresh listener');

        const timeoutId = setTimeout(() => {
          ConsoleEvent.log(
            'Executed refresh token timeout. Removing refres token event listener',
          );
          document.removeEventListener(
            window.__CAAS__.events.refreshToken,
            retryListener,
          );
          this.isRefreshingToken = false;
          reject(new Error('Refresh token timeout'));
        }, FetchUtils.RefreshTokenTimeoutInMs);

        const refreshId = TokenEvent.refresh();
      });
    } else {
      ConsoleEvent.log(
        'A pending refresh is being executed, returning the same promise',
      );
    }
    return this.refreshPromise;
  }

  static async call(url, config) {
    try {
      const response = await fetch(url, {
        ...config,
        headers: this.getHeaders(config.headers),
      });

      if (
        response.status === 401 ||
        (response.status === 400 && response.code === this.MissingTokenMessage)
      ) {
        try {
          const newToken = await this.refreshToken();
          const newHeaders = this.getHeaders({
            authorization: `Bearer ${newToken}`,
          });
          const newResponse = await fetch(url, {
            ...config,
            headers: newHeaders,
          });
          return this.parseResponse(newResponse);
        } catch (e) {
          return Promise.reject(e);
        }
      } else {
        return this.parseResponse(response);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  }

  static async get(url, options = {}) {
    return this.call(url, { ...options, method: this.Methods.GET });
  }

  static async post(url, body = null, options = {}) {
    return this.call(url, {
      ...options,
      method: this.Methods.POST,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  }

  static async put(url, body = null, options = {}) {
    return this.call(url, {
      ...options,
      method: this.Methods.PUT,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  }
}
